import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CalendarClock, Flame } from "lucide-react";
import { historyPeriodsQuery } from "@/lib/history-queries";
import { monthName, monthSlug } from "@/lib/history";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { translate, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";
import { formatHeat } from "@/lib/behot";

const isYear = (v: string) => /^\d{4}$/.test(v);

export const Route = createFileRoute("/history/$year/")({
  loader: async ({ context, params }) => {
    if (!isYear(params.year)) throw notFound();
    await context.queryClient.ensureQueryData(historyPeriodsQuery);
    return { year: Number(params.year) };
  },
  head: ({ params }) => {
    const locale = detectLocale();
    const year = params.year;
    return buildPageMetadata({
      path: `/history/${year}`,
      title: translate(locale, "history.year.title", { year }),
      description: translate(locale, "history.year.description", { year }),
      ogTitle: translate(locale, "history.year.h1", { year }),
      ogDescription: translate(locale, "history.year.description", { year }),
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: [`BeHot.Now rankings ${year}`, `monthly rankings ${year}`],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: translate(locale, "history.nav"), path: "/history" },
        { name: year, path: `/history/${year}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: translate(locale, "history.year.h1", { year }),
        url: `${SITE_URL}/history/${year}`,
        description: translate(locale, "history.year.description", { year }),
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: HistoryYearPage,
});

function HistoryYearPage() {
  const { year } = Route.useLoaderData();
  const { t, locale } = useT();
  const { data: periods } = useSuspenseQuery(historyPeriodsQuery);
  const months = periods.filter((p) => p.year === year);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          BeHot.Now
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/history" className="hover:text-foreground">
          {t("history.nav")}
        </Link>
        <span className="px-1.5">/</span>
        <span className="text-foreground">{year}</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-display text-3xl font-black sm:text-4xl">
          {t("history.year.h1", { year })}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("history.year.intro", { year })}
        </p>
      </header>

      {months.length === 0 ? (
        <p className="mt-10 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("history.index.empty")}
        </p>
      ) : (
        <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {months.map((m) => (
            <li key={m.id}>
              <Link
                to="/history/$year/$month"
                params={{ year: String(m.year), month: monthSlug(m.month) }}
                className="card-surface flex items-center gap-3 p-4 transition-colors hover:border-primary/50"
              >
                <CalendarClock className="size-5 text-primary" />
                <span className="min-w-0 flex-1">
                  <span className="block font-display font-semibold capitalize">
                    {monthName(m.month, locale)} {m.year}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t("history.entries", { n: m.entry_count })}
                    {" · "}
                    <Flame className="inline size-3 text-primary" /> {formatHeat(m.total_heat_cents)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/history"
        className="mt-8 inline-block text-sm font-medium text-primary hover:underline"
      >
        {t("history.backToHistory")}
      </Link>
    </div>
  );
}
